import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import TableManager from '../components/data/TableManager';
import { logger } from '../utils/logger';
import TabelleCustomActions from '../components/Tabelle/TabelleCustomActions';
import useLookupOptions from '../hooks/useLookupOptions';
import { getModuleContextFromOptions, isHubModule } from '../utils/moduli';

// Configuration for SKE_Tabelle columns
const BASE_COLUMN_CONFIG = [
  // --- Keys and Identifiers ---
  "TAB_Id_Tabelle=1,ID Tabella,isPrimaryKey", // PK, form/detail only
  "TAB_IdRif_Tabelle=0,ID Rif. Tabella", // Form/detail only
  "TAB_Nome=2,Nome Tabella,required", // Visible in table & form
  "TAB_NomeSingolare=2,Nome Singolare",
  "TAB_Descrizione=2,Descrizione", // Visible in table & form
  "TAB_Id_Moduli=2,Modulo,required,lookupName=moduliLookup", // Visible in table & form, FK
  "TAB_Prefisso=2,Prefisso", // Visible in table & form
  "TAB_AcronimoCampi=2,Acronimo Campi,required", // Visible in table & form
  "TAB_Motori=0,Motore DB,lookupName=motoriLookup", // Visible in table & form, FK
  
  // --- Flags (Boolean-like integers, treat as booleans in UI) ---
  "TAB_Cronologia=2,Cronologia,type=boolean,isBoolean",
  "TAB_Cache=2,Cache,type=boolean,isBoolean",
  "TAB_UpdateTimestamp=2,Update Timestamp,type=boolean,isBoolean",
  "TAB_GeneraFiltro=2,Genera Filtro,type=boolean,isBoolean",
  "TAB_DatiInserimento=2,Traccia Dati Inserimento,type=boolean,isBoolean",
  "TAB_DatiModifica=2,Traccia Dati Modifica,type=boolean,isBoolean",
  "TAB_View=2,È una Vista,type=boolean,isBoolean",
  "TAB_MultiAzienda=0,Multi Azienda,type=boolean,isBoolean",

        "TAB_CreaItemFiltro=0,Genera Filtro Ricerca,type=boolean,isBoolean",
        "TAB_CreaItem=1,Crea Item Da Tabella,type=boolean,isBoolean",
  // --- Validity and Status ---
  "TAB_InizioValidita=0,Inizio Validità,type=date", // Visible in table & form
  "TAB_FineValidita=0,Fine Validità,type=datetime", // Form/detail only
  "TAB_StatiRecord=0,Stato Record,required,lookupName=statiRecordLookup", // Visible in table & form

  // --- Audit Fields (Hidden, managed by backend/trigger) ---
  "TAB_UtenteInserimento=0,Utente Inserimento",
  "TAB_TerminaleInserimento=0,Terminale Inserimento",
  "TAB_DataOraInserimento=0,Data/Ora Inserimento",
  "TAB_UtenteModifica=0,Utente Modifica",
  "TAB_TerminaleModifica=0,Terminale Modifica",
  "TAB_DataOraModifica=0,Data/Ora Modifica",
  "TAB_Timestamp=0,Timestamp DB",
];

// Configuration for Filters - RIMUOVO TAB_Id_Tabelle 
const filterConfig = [
  { field: 'TAB_Nome', label: 'Nome Tabella', type: 'text' },
  { field: 'TAB_Descrizione', label: 'Descrizione', type: 'text' },
  { field: 'TAB_Id_Moduli', label: 'Modulo', type: 'lookup', lookupName: 'moduliLookup', lookupType: 'select' },
  { field: 'TAB_Prefisso', label: 'Prefisso', type: 'text' },
];

// Configuration for Lookups
const lookupConfig = {
  moduliLookup: {
    tableName: 'SKE_Moduli', // Assuming SKE_Moduli is the table for modules
    valueField: 'MOD_Id_Moduli', // PK of SKE_Moduli
    labelField: 'MOD_CodiceModulo', // Display field from SKE_Moduli (e.g., MOD_CodiceModulo or MOD_DescrizioneModulo)
    additionalFields: ['MOD_DescrizioneModulo'],
  },
  motoriLookup: {
    // Placeholder: Assuming a table SKE_MotoriDB exists for database engines
    tableName: 'SKE_Motori', // Replace with your actual table name for DB engines
    valueField: 'MOT_Id_Motori', // Replace with PK of the engines table
    labelField: 'MOT_Motore', // Replace with display field of the engines table
    // Example with fixed options if no table:
    // options: [
    //   { value: 1, label: 'MySQL' },
    //   { value: 2, label: 'PostgreSQL' },
    //   { value: 3, label: 'SQL Server' },
    // ],
  },
};

const moduleLookupConfig = {
  moduliLookup: lookupConfig.moduliLookup,
};

const EMPTY_MODULE_OPTIONS = Object.freeze([]);
const getConfigField = configEntry => String(configEntry).split('=')[0].trim();

// Specific Input Types for Form Fields
const inputTypes = {
  TAB_Descrizione: 'textarea',
  TAB_IdRif_Tabelle: 'number',
  TAB_Id_Moduli: 'select', // Handled by lookup
  TAB_Motori: 'select', // Handled by lookup
  TAB_InizioValidita: 'date',
  TAB_FineValidita: 'datetime-local',
  // Boolean fields are now primarily defined by 'type=boolean' in columnConfig.
  // TableManager and forms will use col.type to determine boolean nature.
};

const TabellePage = () => {
  const location = useLocation();
  
  const [initialFiltersFromState, setInitialFiltersFromState] = useState({});
  const [activeFilters, setActiveFilters] = useState(null);
  const [tableManagerKey, setTableManagerKey] = useState(0);
  const { lookupOptions: moduleLookupOptions } = useLookupOptions(moduleLookupConfig);
  const moduleOptions = moduleLookupOptions?.moduliLookup || EMPTY_MODULE_OPTIONS;

  const tableIdFromState = useMemo(() => 
    location.state?.presetFilter?.TAB_Id_Tabelle, 
    [location.state]
  );

  useEffect(() => {
    const filters = {};

    // Check for filters from URL parameters (new tab navigation)
    const urlParams = new URLSearchParams(window.location.search);
    const filterKey = urlParams.get('filterKey');
    
    if (filterKey) {
      let filterData = localStorage.getItem(filterKey) || sessionStorage.getItem(filterKey);
      if (filterData) {
        try {
          const parsedData = JSON.parse(filterData);
          if (parsedData.presetFilter?.TAB_Id_Tabelle) {
            filters.TAB_Id_Tabelle = parsedData.presetFilter.TAB_Id_Tabelle;
            logger.info('[TabellePage] Received tableId from new tab navigation:', parsedData.presetFilter.TAB_Id_Tabelle);
          }
          localStorage.removeItem(filterKey);
          sessionStorage.removeItem(filterKey);
        } catch (error) {
          logger.error('[TabellePage] Error parsing filter data from storage:', error);
        }
      }
    } else if (tableIdFromState) {
      filters.TAB_Id_Tabelle = tableIdFromState;
      logger.info('[TabellePage] Received tableId for filtering TAB_Id_Tabelle:', tableIdFromState);
    }

    setInitialFiltersFromState(filters);
    
    if (Object.keys(filters).length > 0) {
      setTableManagerKey(prevKey => prevKey + 1); // Force re-render
    }
  }, [tableIdFromState]);

  // ✅ MODIFICATO: Calcola se fare ricerca automatica con filtri iniziali
  const autoSearchOnInitialFilters = useMemo(() => {
    return Object.keys(initialFiltersFromState).length > 0;
  }, [initialFiltersFromState]);

  // ✅ MODIFICATO: Disabilita autoSearchOnLoad quando ci sono filtri iniziali
  const shouldAutoSearchOnLoad = useMemo(() => {
    return Object.keys(initialFiltersFromState).length === 0;
  }, [initialFiltersFromState]);

  const handleFiltersChange = useCallback(filters => {
    setActiveFilters(filters || {});
  }, []);

  const effectiveFilters = activeFilters === null ? initialFiltersFromState : activeFilters;
  const gridModuleContext = useMemo(
    () => getModuleContextFromOptions(effectiveFilters.TAB_Id_Moduli, moduleOptions),
    [effectiveFilters.TAB_Id_Moduli, moduleOptions]
  );
  const isGridHub = useMemo(() => isHubModule(gridModuleContext), [gridModuleContext]);

  // Nel contesto misto la colonna Milo resta nascosta. Diventa una colonna di
  // griglia solo quando il filtro TAB_Id_Moduli risolve esplicitamente a HUB.
  const columnConfig = useMemo(
    () => isGridHub
      ? BASE_COLUMN_CONFIG
      : BASE_COLUMN_CONFIG.filter(entry => getConfigField(entry) !== 'TAB_NomeSingolare'),
    [isGridHub]
  );

  const isTabelleFormFieldVisible = useCallback((column, formData) => {
    if (column.field !== 'TAB_NomeSingolare') return true;
    const moduleContext = getModuleContextFromOptions(formData?.TAB_Id_Moduli, moduleOptions);
    return isHubModule(moduleContext);
  }, [moduleOptions]);

  return (
    <TableManager
      key={`tabelle-manager-${tableManagerKey}`} // Key change forces re-mount
      tableName="SKE_Tabelle"
      primaryKey="TAB_Id_Tabelle"
      pageTitle="Gestione Tabelle"
      columnConfig={columnConfig}
      formColumnConfig={BASE_COLUMN_CONFIG}
      isFormFieldVisible={isTabelleFormFieldVisible}
      filterConfig={filterConfig}
      lookupConfig={lookupConfig}
      lookupOptions={moduleLookupOptions}
      inputTypes={inputTypes}
      enableCreate={true}
      enableEdit={true}
      enableDelete={true}
      autoSearchOnLoad={shouldAutoSearchOnLoad} // ✅ MODIFICATO: Condizionale
      defaultSortField="TAB_Id_Tabelle"
      defaultSortDirection="desc"
      initialFilters={initialFiltersFromState}
      autoSearchOnInitialFilters={autoSearchOnInitialFilters}
      onFiltersChange={handleFiltersChange}
    >
      <TabelleCustomActions />
    </TableManager>
  );
};

export default TabellePage;
